import React from "react";
import { api, formatPHP } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Sun, Moon, Star } from "lucide-react";

const TYPES = [
  { key: "day_use", label: "Day Use", icon: Sun },
  { key: "night_use", label: "Night Use", icon: Moon },
  { key: "whole_day", label: "Overnight Use", icon: Star },
];

export default function AdminEditBookingDialog({ booking, onClose, onSaved }) {
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (booking) {
      setForm({
        guest_name: booking.guest_name || "",
        guest_email: booking.guest_email || "",
        guest_phone: booking.guest_phone || "",
        num_guests: booking.num_guests || 1,
        booking_type: booking.booking_type || "day_use",
        booking_date: booking.booking_date || "",
        num_days: booking.num_days || 1,
        total_price: booking.total_price || 0,
        downpayment_amount: booking.downpayment_amount || 0,
        special_requests: booking.special_requests || "",
        status: booking.status || "pending",
      });
    } else {
      setForm(null);
    }
  }, [booking]);

  if (!booking || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const balance = Math.max(
    Number(form.total_price || 0) - Number(form.downpayment_amount || 0),
    0
  );

  const submit = async () => {
    if (!form.guest_name.trim()) return toast.error("Guest name is required");
    if (Number(form.total_price) < 0) return toast.error("Price cannot be negative");
    if (Number(form.downpayment_amount) < 0) return toast.error("Downpayment cannot be negative");
    if (Number(form.downpayment_amount) > Number(form.total_price)) {
      return toast.error("Downpayment cannot exceed total price");
    }
    if (!form.booking_date) return toast.error("Booking date is required");
    if (Number(form.num_days) < 1) return toast.error("Number of days must be at least 1");

    setSaving(true);
    try {
      await api.put(`/bookings/${booking.id}`, {
        guest_name: form.guest_name.trim(),
        guest_email: (form.guest_email || "").trim(),
        guest_phone: (form.guest_phone || "").trim(),
        num_guests: Number(form.num_guests) || 1,
        booking_type: form.booking_type,
        booking_date: form.booking_date,
        num_days: Number(form.num_days) || 1,
        total_price: Number(form.total_price) || 0,
        downpayment_amount: Number(form.downpayment_amount) || 0,
        special_requests: form.special_requests || "",
        status: form.status,
      });
      toast.success("Booking updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update booking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#0A0B10] border-[var(--border)] max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="edit-booking-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Edit Booking</DialogTitle>
          <DialogDescription className="text-[#A1A4B5] text-xs">
            Update any field below. Date/type changes are conflict-checked. Times shown in PHT.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Guest Name *">
              <Input data-testid="edit-guest-name" value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
            <Field label="Phone">
              <Input data-testid="edit-guest-phone" value={form.guest_phone} onChange={(e) => set("guest_phone", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
            <Field label="Email">
              <Input data-testid="edit-guest-email" type="email" value={form.guest_email} onChange={(e) => set("guest_email", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
            <Field label="Guests">
              <Input data-testid="edit-num-guests" type="number" min="1" value={form.num_guests} onChange={(e) => set("num_guests", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
          </div>

          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Booking Type</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {TYPES.map((t) => {
                const active = form.booking_type === t.key;
                const Icon = t.icon;
                return (
                  <button key={t.key} type="button" data-testid={`edit-type-${t.key}`} onClick={() => set("booking_type", t.key)} className={`p-3 rounded-lg border text-xs flex flex-col items-center gap-1 transition-all ${active ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]" : "border-[var(--border)] text-[#A1A4B5] hover:border-[#C5A059]/40"}`}>
                    <Icon size={18} />
                    {t.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#A1A4B5] mt-1.5">
              Admin override: Day Use & Night Use can be set to any weekday. Overnight Use blocks the entire day.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Start Date (PHT) *">
              <Input data-testid="edit-booking-date" type="date" value={form.booking_date} onChange={(e) => set("booking_date", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
            <Field label="Number of Days">
              <Input data-testid="edit-num-days" type="number" min="1" max="30" value={form.num_days} onChange={(e) => set("num_days", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Total Price (₱)">
              <Input data-testid="edit-total-price" type="number" min="0" value={form.total_price} onChange={(e) => set("total_price", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
            <Field label="Downpayment (₱)">
              <Input data-testid="edit-downpayment" type="number" min="0" value={form.downpayment_amount} onChange={(e) => set("downpayment_amount", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
            </Field>
          </div>

          <div className="rounded-lg border border-[var(--border)] bg-[#13151D] p-3 text-xs text-[#A1A4B5] flex justify-between">
            <span>Balance on arrival</span>
            <span className="text-white font-medium">{formatPHP(balance)}</span>
          </div>

          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="edit-status-select" className="bg-[#13151D] border-[var(--border)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#13151D] border-[var(--border)]">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="on_hold">On Hold (1 month)</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            {form.status === "on_hold" && (
              <p className="text-[11px] text-orange-400/80 mt-2">
                ⓘ The booked date is freed up immediately. Downpayment held for 1 month. Edit again later with new dates and flip back to Confirmed.
              </p>
            )}
          </Field>

          <Field label="Special Requests / Notes">
            <Textarea data-testid="edit-special-requests" rows={3} value={form.special_requests} onChange={(e) => set("special_requests", e.target.value)} className="bg-[#13151D] border-[var(--border)]" />
          </Field>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="bg-transparent border-[var(--border)]" data-testid="edit-cancel">Cancel</Button>
          <Button type="button" onClick={submit} disabled={saving} className="gold-btn rounded-full" data-testid="edit-save">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
